import {
  asLocale,
  configureApiClient,
  getLocale,
  getOrCreateDeviceUuid,
  intlLocale,
  recordServerTime,
  setLocale,
} from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import { App } from './App';
import { API_BASE_URL, IDB_NAME } from './app/config';
import { store } from './app/store';
import { initI18n } from './i18n';
import './index.css';

/**
 * Nothing renders until the device identity and the locale are known.
 *
 * Both come from IndexedDB, both are asynchronous, and both are needed by the
 * very first request the app makes: a login without a `deviceUuid` can only
 * be refused (D-06), and a screen rendered in the wrong language then
 * repainted is worse than a hundred milliseconds of blank.
 */
async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('index.html is missing #root');

  const deviceUuid = await getOrCreateDeviceUuid(IDB_NAME);

  // Client-side half of the amendment #20 precedence: stored choice, then the
  // browser's language, then French.
  let locale: Locale = (await getLocale(IDB_NAME)) ?? asLocale(navigator.language) ?? 'fr';

  configureApiClient({
    baseUrl: API_BASE_URL,
    getLocale: () => locale,
    getDeviceUuid: () => deviceUuid,
    getUserAgent: () => navigator.userAgent,
    // Every response re-anchors the clock (D-10).
    onServerTime: recordServerTime,
  });

  const i18n = initI18n(locale);
  document.documentElement.lang = intlLocale(locale);

  i18n.on('languageChanged', (next: string) => {
    locale = asLocale(next) ?? 'fr';
    document.documentElement.lang = intlLocale(locale);
    // Pre-auth this is the only place the choice survives a reload; once
    // authenticated the shell also posts it to `/me/locale`.
    void setLocale(IDB_NAME, locale);
  });

  createRoot(container).render(
    <StrictMode>
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <App />
        </I18nextProvider>
      </Provider>
    </StrictMode>,
  );
}

void bootstrap();
